import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, NavLink, Link } from 'react-router-dom';
import { auth, validation } from './services/api';
import { NdfProvider } from './NdfContext';
import Login from './components/Login';
import MesDepenses from './components/MesDepenses';
import FicheDepense from './components/FicheDepense';
import FicheKm from './components/FicheKm';
import MesNotes from './components/MesNotes';
import NoteDetail from './components/NoteDetail';
import Validation from './components/Validation';
import Comptabilite from './components/Comptabilite';
import Administration from './components/Administration';
import { IcCamera, IcRecu, IcCoche, IcLivre, IcReglages, IcSortie } from './components/Icones';
import './App.css';

// Gabarit commun : en-tête A97 + navigation latérale sur grand écran,
// barre d'onglets en bas sur mobile (l'app se vit d'abord au téléphone).
function Layout({ user, onLogout }) {
  const [enAttente, setEnAttente] = useState(0);
  const estValideur = user.role === 'valideur' || user.role === 'admin';
  const estComptable = user.role === 'comptable' || user.role === 'admin';

  useEffect(() => {
    if (!estValideur) return undefined;
    const charger = () => validation.enAttente()
      .then((res) => setEnAttente(res.data.notes.length))
      .catch(() => {});
    charger();
    const t = setInterval(charger, 60000);
    return () => clearInterval(t);
  }, [estValideur]);

  const onglets = [
    { to: '/', label: 'Dépenses', icone: <IcCamera />, fin: true },
    { to: '/notes', label: 'Notes', icone: <IcRecu /> },
    ...(estValideur ? [{ to: '/validation', label: 'Validation', icone: <IcCoche />, badge: enAttente }] : []),
    ...(estComptable ? [{ to: '/compta', label: 'Compta', icone: <IcLivre /> }] : []),
    ...(user.role === 'admin' ? [{ to: '/admin', label: 'Admin', icone: <IcReglages /> }] : []),
  ];

  const rendreOnglet = (o) => (
    <NavLink
      key={o.to}
      to={o.to}
      end={o.fin}
      className={({ isActive }) => `nf-nav-item ${isActive ? 'active' : ''}`}
    >
      <span className="nf-nav-icone" aria-hidden="true">{o.icone}</span>
      <span className="nf-nav-label">{o.label}</span>
      {o.badge > 0 && <span className="nf-nav-badge">{o.badge}</span>}
    </NavLink>
  );

  return (
    <div className="nf-shell">
      <header className="nf-header">
        <Link to="/" className="nf-brand">
          A<span className="nf-gold">97</span>
          <span className="nf-brand-app">NDF</span>
        </Link>
        <div className="nf-header-user">
          <span className="nf-header-nom">{user.prenom ? `${user.prenom} ${user.nom || ''}`.trim() : user.email}</span>
          <button className="nf-logout" onClick={onLogout} aria-label="Se déconnecter" title="Se déconnecter">
            <IcSortie taille={19} />
          </button>
        </div>
      </header>
      <div className="nf-layout">
        <nav className="nf-sidebar" aria-label="Navigation">{onglets.map(rendreOnglet)}</nav>
        <main className="nf-main">
          <Outlet />
        </main>
      </div>
      <nav className="nf-tabbar" aria-label="Navigation mobile">{onglets.map(rendreOnglet)}</nav>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  // Garde-fou contre le double-échange du code OIDC en StrictMode (même patron
  // que le CRM, BI et DATA).
  const oidcHandled = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // ?jeton=<jwt> pose le jeton et recharge. Actif en développement, et en
    // production UNIQUEMENT si le build a été fait avec
    // REACT_APP_ACCES_TEMPORAIRE=1 (pont d'accès avant Entra — retiré par un
    // simple rebuild sans le drapeau, cf. scripts/deploy.sh).
    if ((process.env.NODE_ENV === 'development' || process.env.REACT_APP_ACCES_TEMPORAIRE === '1')
        && params.get('jeton')) {
      localStorage.setItem('ndf-token', params.get('jeton'));
      window.location.replace('/');
      return;
    }

    const retourOidc = window.location.pathname === '/oidc/callback' || params.has('code');
    if (retourOidc && !oidcHandled.current) {
      oidcHandled.current = true;
      traiterRetourOidc(params);
    } else {
      verifierSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifierSession = async () => {
    if (localStorage.getItem('ndf-token')) {
      try {
        const res = await auth.getMe();
        setUser(res.data.user);
      } catch (e) {
        localStorage.removeItem('ndf-token');
      }
    }
    setLoading(false);
  };

  const traiterRetourOidc = async (params) => {
    const nettoyerUrl = () => window.history.replaceState({}, '', '/');
    try {
      const erreurOauth = params.get('error_description') || params.get('error');
      if (erreurOauth) throw new Error(erreurOauth);
      const code = params.get('code');
      const state = params.get('state');
      if (!code || !state) throw new Error('Réponse Microsoft incomplète');
      const res = await auth.microsoftCallback(code, state);
      nettoyerUrl();
      localStorage.setItem('ndf-token', res.data.token);
      setUser(res.data.user);
    } catch (error) {
      nettoyerUrl();
      setAuthError(error.response?.data?.error || error.message || 'Échec de la connexion Microsoft');
    } finally {
      setLoading(false);
    }
  };

  const seDeconnecter = () => {
    localStorage.removeItem('ndf-token');
    setUser(null);
  };

  if (loading) return null;
  if (!user) return <Login authError={authError} />;

  return (
    <BrowserRouter>
      <NdfProvider user={user}>
        <Routes>
          <Route element={<Layout user={user} onLogout={seDeconnecter} />}>
            <Route index element={<MesDepenses />} />
            <Route path="depense/:id" element={<FicheDepense />} />
            <Route path="km" element={<FicheKm />} />
            <Route path="notes" element={<MesNotes />} />
            <Route path="note/:id" element={<NoteDetail />} />
            <Route path="validation" element={<Validation />} />
            <Route path="compta" element={<Comptabilite />} />
            <Route path="admin" element={<Administration />} />
            <Route path="*" element={<MesDepenses />} />
          </Route>
        </Routes>
      </NdfProvider>
    </BrowserRouter>
  );
}

export default App;
